import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Tabs, 
  Tab, 
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';

export type NavigationSection = 'users' | 'locations' | 'attachmentTypes' | 'customValues' | 'productIdentifiers' | 'documents';

interface MainNavigationProps {
  activeSection?: NavigationSection;
  onSectionChange: (section: NavigationSection) => void;
}

const MainNavigation: React.FC<MainNavigationProps> = ({ 
  activeSection = 'users',
  onSectionChange 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const handleChange = (_event: React.SyntheticEvent, newValue: NavigationSection) => {
    onSectionChange(newValue);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileMenuItemClick = (section: NavigationSection) => {
    onSectionChange(section);
    handleMobileMenuClose();
  };

  // Temporary flag to show only Users menu item
  const showOnlyUsers = true; // Set to false to show all menu items again
  
  // Navigation items configuration
  const allNavigationItems = [
    { value: 'users', label: t('navigation.users', 'USERS') },
    { value: 'locations', label: t('navigation.locations', 'LOCATIONS') },
    { value: 'attachmentTypes', label: t('navigation.attachmentTypes', 'ATTACHMENT TYPES') },
    { value: 'customValues', label: t('navigation.customValues', 'CUSTOM VALUES') },
    { value: 'productIdentifiers', label: t('navigation.productIdentifiers', 'PRODUCT IDENTIFIERS') },
    { value: 'documents', label: t('navigation.documents', 'DOCUMENTS') }
  ];
  
  // Filter navigation items based on the temporary flag
  const navigationItems = showOnlyUsers
    ? allNavigationItems.filter(item => item.value === 'users')
    : allNavigationItems;

  // Desktop navigation
  const desktopNavigation = (
    <Tabs 
      value={activeSection}
      onChange={handleChange}
      variant="standard"
      sx={{
        '& .MuiTabs-indicator': {
          backgroundColor: theme.palette.primary.main,
          height: 2
        },
        '& .MuiTab-root': {
          fontWeight: 400,
          fontSize: '0.85rem',
          color: theme.palette.text.primary,
          letterSpacing: '0.5px',
          minWidth: 'auto',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&.Mui-selected': {
            color: theme.palette.text.primary,
            fontWeight: 500
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)'
          }
        }
      }}
    >
      {navigationItems.map((item) => (
        <Tab 
          key={item.value} 
          label={item.label} 
          value={item.value} 
          sx={{
            textTransform: 'uppercase',
            minWidth: 0
          }}
        />
      ))}
    </Tabs>
  );

  // Mobile navigation
  const mobileNavigation = (
    <>
      <IconButton
        color="inherit"
        aria-label="open menu"
        onClick={handleMobileMenuOpen}
        sx={{ ml: 'auto' }}
      >
        <MenuIcon />
      </IconButton>
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: '200px',
            maxWidth: '100%'
          }
        }}
      >
        {navigationItems.map((item) => (
          <MenuItem 
            key={item.value}
            onClick={() => handleMobileMenuItemClick(item.value as NavigationSection)}
            selected={activeSection === item.value}
            sx={{
              color: 'inherit',
              fontWeight: activeSection === item.value ? 500 : 400,
              borderBottom: activeSection === item.value ? `2px solid ${theme.palette.primary.main}` : 'none'
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  // If showOnlyUsers is true, don't render the navigation at all
  if (showOnlyUsers) {
    return null;
  }

  return (
    <Box
      className="main-navigation"
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        mb: 2,
        px: 2
      }}
    >
      {isMobile ? mobileNavigation : desktopNavigation}
    </Box>
  );
};

export default MainNavigation;